import { BRAND } from '@/config/brand';

export const USER_GUIDE_VERSION = "1.0";
export const USER_GUIDE_DATE = "Janvier 2025";

export interface GuideSection {
  id: string;
  title: string;
  icon: string;
  description: string;
  howToUse: string[];
  bestPractices: string[];
  commonMistakes: string[];
}

export const guideSections: GuideSection[] = [
  {
    id: "introduction",
    title: "Introduction & Vue d'ensemble",
    icon: "🏠",
    description: `${BRAND.name.full} est une plateforme professionnelle complète dédiée aux praticiens en naturopathie et iridologie. Elle vous permet de gérer l'ensemble de votre activité : consultants, rendez-vous, bilans, facturation, communication et bien plus encore.`,
    howToUse: [
      "Connectez-vous à l'espace professionnel via /auth",
      "Le tableau de bord central (/dashboard) affiche tous les modules disponibles",
      "Chaque bouton mène à une fonctionnalité spécifique",
      "Utilisez le menu de navigation en haut pour accéder rapidement aux sections principales"
    ],
    bestPractices: [
      "Commencez par configurer vos informations praticien dans 'Paramètres du compte'",
      "Configurez vos horaires de disponibilité avant d'activer les réservations",
      "Ajoutez votre signature pour les documents PDF officiels",
      "Testez chaque fonctionnalité avec un consultant fictif avant usage réel"
    ],
    commonMistakes: [
      "Ne pas configurer les informations praticien avant de générer des factures",
      "Oublier de définir les horaires de disponibilité",
      "Ne pas sauvegarder régulièrement les modifications"
    ]
  },
  {
    id: "consultants",
    title: "Consultants",
    icon: "👥",
    description: "Gestion de votre base de consultants (patients). Créez des fiches détaillées avec coordonnées, historique médical, allergies et traitements en cours.",
    howToUse: [
      "Accédez via 'Consultants' sur le tableau de bord",
      "Cliquez sur 'Nouveau consultant' pour créer une fiche",
      "Renseignez les informations : nom, date de naissance, email, téléphone, adresse",
      "Ajoutez l'historique médical, allergies connues et médicaments",
      "Cliquez sur un consultant pour voir sa fiche détaillée et son historique"
    ],
    bestPractices: [
      "Remplissez systématiquement le champ 'Historique médical'",
      "Notez toutes les allergies connues pour éviter les contre-indications",
      "Mettez à jour les médicaments en cours à chaque consultation",
      "Utilisez le champ email pour envoyer les documents automatiquement"
    ],
    commonMistakes: [
      "Créer des doublons de consultants (vérifiez d'abord s'il existe)",
      "Oublier de noter les allergies et contre-indications",
      "Ne pas mettre à jour les informations après chaque consultation"
    ]
  },
  {
    id: "appointments",
    title: "Rendez-vous",
    icon: "📅",
    description: "Gestion de votre agenda de consultations. Visualisez, créez et gérez vos rendez-vous en cabinet ou en visioconférence.",
    howToUse: [
      "Accédez via 'Rendez-vous' sur le tableau de bord",
      "Visualisez tous les rendez-vous à venir et passés",
      "Créez un nouveau rendez-vous en sélectionnant un consultant et un créneau",
      "Choisissez le type : cabinet ou visioconférence",
      "Les notifications sont envoyées automatiquement au consultant"
    ],
    bestPractices: [
      "Configurez d'abord vos horaires dans 'Horaires'",
      "Activez les rappels automatiques par email",
      "Prévoyez un temps tampon entre les rendez-vous",
      "Notez les motifs de consultation pour préparer la séance"
    ],
    commonMistakes: [
      "Prendre des rendez-vous sans avoir configuré les horaires",
      "Oublier de confirmer les rendez-vous manuellement si nécessaire",
      "Ne pas vérifier les chevauchements d'horaires"
    ]
  },
  {
    id: "vitality",
    title: "Bilan de Vitalité",
    icon: "💪",
    description: "Outil complet d'évaluation de la vitalité du consultant. Questionnaire structuré couvrant tous les aspects : alimentation, sommeil, stress, activité physique, etc.",
    howToUse: [
      "Accédez via 'Bilan vitalité' sur le tableau de bord",
      "Sélectionnez le consultant concerné",
      "Remplissez les différentes sections du questionnaire",
      "Les systèmes corporels sont évalués automatiquement",
      "Exportez le bilan en PDF pour l'archivage ou l'envoi"
    ],
    bestPractices: [
      "Prenez le temps de remplir toutes les sections",
      "Faites participer le consultant pendant la consultation",
      "Notez les observations personnelles dans les champs libres",
      "Exportez systématiquement en PDF après chaque bilan"
    ],
    commonMistakes: [
      "Sauter des sections importantes du questionnaire",
      "Ne pas sauvegarder avant de quitter la page",
      "Oublier d'exporter le PDF final"
    ]
  },
  {
    id: "session-report",
    title: "Compte Rendu de Séance",
    icon: "📝",
    description: "Génération de comptes rendus professionnels après consultation. Inclut les observations, recommandations, protocoles et plan d'action personnalisé.",
    howToUse: [
      "Accédez via 'Compte rendu' sur le tableau de bord",
      "Sélectionnez le consultant et la date de la séance",
      "Complétez les sections : observations, recommandations, protocole",
      "Ajoutez les recommandations issues de l'analyse (plantes, HE, etc.)",
      "Générez le PDF et envoyez-le par email au consultant"
    ],
    bestPractices: [
      "Rédigez le compte rendu immédiatement après la consultation",
      "Soyez précis dans les posologies et durées de cure",
      "Incluez les contre-indications personnalisées",
      "Envoyez le compte rendu dans les 24h suivant la séance"
    ],
    commonMistakes: [
      "Rédiger le compte rendu trop longtemps après la séance",
      "Oublier d'inclure les mises en garde personnalisées",
      "Ne pas vérifier la cohérence des recommandations"
    ]
  },
  {
    id: "accompaniments",
    title: "Accompagnements",
    icon: "❤️",
    description: "Gestion de vos offres d'accompagnement (prestations). Définissez vos services, tarifs, durées et conditions de réservation.",
    howToUse: [
      "Accédez via 'Accompagnements' sur le tableau de bord",
      "Créez un nouvel accompagnement avec titre, description, prix et durée",
      "Activez/désactivez la réservation en ligne (cabinet et/ou visio)",
      "Réorganisez l'ordre d'affichage par glisser-déposer",
      "Activez/désactivez chaque accompagnement selon vos besoins"
    ],
    bestPractices: [
      "Rédigez des descriptions claires et complètes",
      "Indiquez précisément la durée de chaque accompagnement",
      "Différenciez les offres cabinet et visio si nécessaire",
      "Mettez à jour les tarifs régulièrement"
    ],
    commonMistakes: [
      "Descriptions trop vagues qui n'informent pas le client",
      "Prix non cohérents avec le marché",
      "Oublier d'activer la réservation en ligne"
    ]
  },
  {
    id: "availability",
    title: "Horaires",
    icon: "🕐",
    description: "Configuration de vos disponibilités hebdomadaires. Définissez vos créneaux de consultation pour permettre les réservations en ligne.",
    howToUse: [
      "Accédez via 'Horaires' sur le tableau de bord",
      "Pour chaque jour, définissez vos plages horaires",
      "Indiquez si le créneau est cabinet uniquement, visio uniquement, ou les deux",
      "Sauvegardez vos modifications",
      "Les créneaux sont automatiquement proposés lors des réservations"
    ],
    bestPractices: [
      "Prévoyez des pauses entre les consultations (15-30 min)",
      "Bloquez les jours de formation ou congés",
      "Différenciez les créneaux cabinet et visio selon votre organisation",
      "Révisez vos horaires chaque trimestre"
    ],
    commonMistakes: [
      "Créneaux trop rapprochés sans temps de transition",
      "Oublier de bloquer les périodes d'absence",
      "Horaires incohérents avec les accompagnements proposés"
    ]
  },
  {
    id: "billing",
    title: "Facturation",
    icon: "💰",
    description: "Gestion complète de votre facturation. Créez des factures professionnelles, suivez les paiements et exportez pour votre comptabilité.",
    howToUse: [
      "Accédez via 'Facturation' sur le tableau de bord",
      "Visualisez les rendez-vous facturables et commandes produits",
      "Créez une facture en sélectionnant les éléments à facturer",
      "La facture est générée avec votre branding et numérotation automatique",
      "Envoyez la facture par email ou téléchargez le PDF"
    ],
    bestPractices: [
      "Vérifiez vos informations praticien (SIRET, adresse) avant de facturer",
      "Ajoutez votre signature aux factures pour plus de professionnalisme",
      "Facturez rapidement après chaque prestation",
      "Archivez systématiquement les factures générées"
    ],
    commonMistakes: [
      "Informations praticien incomplètes sur les factures",
      "Oublier de noter le mode de paiement",
      "Ne pas vérifier les montants avant envoi"
    ]
  },
  {
    id: "events",
    title: "Événements",
    icon: "📆",
    description: "Création et gestion de vos ateliers, conférences et événements. Permettez les inscriptions en ligne et gérez les participants.",
    howToUse: [
      "Accédez via 'Événements' sur le tableau de bord",
      "Créez un événement avec titre, description, date, lieu et prix",
      "Définissez le nombre maximum de participants",
      "Activez les inscriptions en ligne",
      "Suivez les inscriptions et envoyez des confirmations"
    ],
    bestPractices: [
      "Rédigez des descriptions détaillées avec objectifs et programme",
      "Ajoutez une image attractive pour chaque événement",
      "Limitez les places pour créer de l'engagement",
      "Envoyez des rappels aux inscrits avant l'événement"
    ],
    commonMistakes: [
      "Description insuffisante qui ne motive pas l'inscription",
      "Oublier de désactiver les inscriptions une fois complet",
      "Ne pas confirmer les inscriptions par email"
    ]
  },
  {
    id: "shop",
    title: "Boutique",
    icon: "🛍️",
    description: "Gestion de votre boutique en ligne. Vendez vos produits (compléments, tisanes, etc.) directement depuis votre site.",
    howToUse: [
      "Accédez via 'Boutique' sur le tableau de bord",
      "Ajoutez un produit avec titre, description, prix et catégorie",
      "Uploadez une photo de qualité pour chaque produit",
      "Gérez les stocks et activez/désactivez les produits",
      "Les commandes apparaissent dans l'onglet Facturation"
    ],
    bestPractices: [
      "Photos de qualité professionnelle pour chaque produit",
      "Descriptions incluant composition, bienfaits et précautions",
      "Mettez à jour les stocks régulièrement",
      "Répondez rapidement aux commandes"
    ],
    commonMistakes: [
      "Photos floues ou de mauvaise qualité",
      "Descriptions incomplètes (manque de composition ou précautions)",
      "Oublier de mettre à jour les stocks"
    ]
  },
  {
    id: "seasonal-wellness",
    title: "Bien-être Saisonnier",
    icon: "🌿",
    description: "Conseils saisonniers pour vos consultants. Partagez des recommandations adaptées à chaque saison : alimentation, hygiène de vie, soutiens naturels.",
    howToUse: [
      "Accédez via 'Bien-être saisonnier' sur le tableau de bord",
      "Sélectionnez la saison à éditer",
      "Complétez les sections : conseils alimentaires, hygiène de vie, soutiens naturels, routine",
      "Activez/désactivez l'affichage de chaque saison",
      "Les conseils sont affichés sur la vitrine publique"
    ],
    bestPractices: [
      "Préparez les contenus en avance de chaque saison",
      "Soyez spécifique et actionnable dans vos conseils",
      "Ajoutez des images évocatrices pour chaque saison",
      "Actualisez les contenus chaque année"
    ],
    commonMistakes: [
      "Conseils trop génériques sans valeur ajoutée",
      "Oublier d'activer la saison en cours",
      "Ne pas mettre à jour le contenu d'une année sur l'autre"
    ]
  },
  {
    id: "gift-cards",
    title: "Cartes Cadeaux",
    icon: "🎁",
    description: "Système de cartes cadeaux pour vos clients. Permettez l'achat de cartes cadeaux en ligne avec paiement Stripe intégré.",
    howToUse: [
      "Accédez via 'Cartes Cadeaux' sur le tableau de bord",
      "Configurez les montants prédéfinis et/ou montants personnalisés",
      "Définissez la durée de validité des cartes",
      "Les clients achètent directement sur votre vitrine",
      "Gérez et validez les cartes lors des consultations"
    ],
    bestPractices: [
      "Proposez plusieurs montants pour différents budgets",
      "Définissez une validité suffisante (12 mois recommandé)",
      "Vérifiez la validité des cartes avant application",
      "Suivez les cartes non utilisées"
    ],
    commonMistakes: [
      "Validité trop courte qui frustre les clients",
      "Ne pas vérifier si la carte a déjà été utilisée",
      "Oublier de déduire le montant lors de l'utilisation"
    ]
  },
  {
    id: "iridology",
    title: "Iridologie",
    icon: "👁️",
    description: "Module complet d'analyse iridologique. Capturez les photos d'iris, analysez les zones, notez les observations et générez des rapports détaillés.",
    howToUse: [
      "Accédez via 'Iridologie' sur le tableau de bord",
      "Créez une nouvelle session pour un consultant",
      "Capturez ou uploadez les photos de l'iris (œil droit et gauche)",
      "Utilisez la carte interactive pour noter les observations par zone",
      "Remplissez le formulaire d'observations générales",
      "Générez le rapport PDF complet avec recommandations"
    ],
    bestPractices: [
      "Utilisez un bon éclairage pour les photos d'iris",
      "Prenez plusieurs photos et gardez les meilleures",
      "Notez systématiquement les observations par zone",
      "Complétez les recommandations avant export PDF",
      "Comparez avec les sessions précédentes pour suivre l'évolution"
    ],
    commonMistakes: [
      "Photos floues ou mal éclairées",
      "Oublier de noter les observations sur la carte interactive",
      "Ne pas remplir les recommandations avant export",
      "Confondre œil droit et œil gauche"
    ]
  },
  {
    id: "medicinal-plants",
    title: "Plantes Médicinales",
    icon: "🌱",
    description: "Base de données des plantes médicinales. Consultez les fiches détaillées : propriétés, indications, contre-indications, posologies.",
    howToUse: [
      "Accédez via 'Plantes médicinales' sur le tableau de bord",
      "Recherchez une plante par nom latin ou vernaculaire",
      "Consultez la fiche détaillée : description, parties utilisées, propriétés",
      "Vérifiez les contre-indications et interactions",
      "Utilisez ces informations pour vos recommandations"
    ],
    bestPractices: [
      "Vérifiez toujours les contre-indications avant de recommander",
      "Croisez avec les médicaments du consultant",
      "Notez les sources de vos recommandations",
      "Mettez à jour vos connaissances régulièrement"
    ],
    commonMistakes: [
      "Recommander sans vérifier les contre-indications",
      "Ignorer les interactions médicamenteuses",
      "Posologies inappropriées au profil du consultant"
    ]
  },
  {
    id: "bach-flowers",
    title: "Fleurs de Bach",
    icon: "🌸",
    description: "Base de données des 38 Fleurs de Bach. Consultez les indications émotionnelles, associations et protocoles d'utilisation.",
    howToUse: [
      "Accédez via 'Fleurs de Bach' sur le tableau de bord",
      "Recherchez par nom ou par émotion/état",
      "Consultez les indications spécifiques de chaque fleur",
      "Créez des mélanges personnalisés (max 7 fleurs)",
      "Intégrez les recommandations dans vos comptes rendus"
    ],
    bestPractices: [
      "Limitez les mélanges à 5-7 fleurs maximum",
      "Choisissez les fleurs selon l'état émotionnel actuel",
      "Expliquez l'utilisation au consultant (4 gouttes, 4x/jour)",
      "Réévaluez après 3 semaines d'utilisation"
    ],
    commonMistakes: [
      "Mélanger trop de fleurs (dilution de l'effet)",
      "Choisir selon l'historique plutôt que l'état actuel",
      "Oublier d'expliquer la posologie au consultant"
    ]
  },
  {
    id: "analysis",
    title: "Analyse",
    icon: "🔬",
    description: "Outil d'analyse des symptômes avec recommandations automatiques. Saisissez les symptômes et obtenez des suggestions de plantes et traitements.",
    howToUse: [
      "Accédez via 'Analyse' sur le tableau de bord",
      "Sélectionnez le consultant concerné",
      "Saisissez les symptômes observés",
      "L'algorithme propose des recommandations classées par pertinence",
      "Sélectionnez et personnalisez les recommandations retenues",
      "Intégrez-les au compte rendu de séance"
    ],
    bestPractices: [
      "Soyez précis dans la description des symptômes",
      "Vérifiez les contre-indications avec le profil du consultant",
      "Personnalisez les posologies selon l'âge et le terrain",
      "Utilisez comme aide à la décision, pas comme prescription automatique"
    ],
    commonMistakes: [
      "Accepter les recommandations sans vérification",
      "Ignorer les alertes de contre-indication",
      "Symptômes trop vagues donnant des résultats imprécis"
    ]
  },
  {
    id: "library",
    title: "Bibliothèque",
    icon: "📚",
    description: "Accès aux différentes bibliothèques de référence : aromathérapie, gemmothérapie, hydrolats, huiles végétales, argiles, apithérapie.",
    howToUse: [
      "Accédez via 'Bibliothèque' sur le tableau de bord",
      "Choisissez la bibliothèque souhaitée",
      "Recherchez par nom ou par indication",
      "Consultez les fiches détaillées",
      "Utilisez les informations pour enrichir vos recommandations"
    ],
    bestPractices: [
      "Consultez régulièrement pour enrichir vos connaissances",
      "Croisez les informations entre bibliothèques",
      "Notez les synergies intéressantes",
      "Vérifiez toujours les précautions d'emploi"
    ],
    commonMistakes: [
      "Se fier uniquement à une source",
      "Ignorer les précautions d'emploi spécifiques",
      "Ne pas vérifier les interactions entre produits"
    ]
  },
  {
    id: "newsletter",
    title: "Newsletter",
    icon: "📧",
    description: "Création et envoi de newsletters à vos abonnés. Rédigez des contenus, planifiez les envois et suivez les statistiques.",
    howToUse: [
      "Accédez via 'Newsletter' sur le tableau de bord",
      "Créez une nouvelle newsletter avec sujet et contenu",
      "Utilisez l'éditeur pour formater votre message",
      "Prévisualisez avant envoi",
      "Programmez l'envoi ou envoyez immédiatement",
      "Consultez les statistiques d'ouverture"
    ],
    bestPractices: [
      "Envoyez régulièrement (1-2x par mois maximum)",
      "Objets de mail courts et engageants",
      "Contenu de valeur (conseils, actualités, promotions)",
      "Incluez toujours un lien de désabonnement",
      "Testez l'affichage sur mobile"
    ],
    commonMistakes: [
      "Envois trop fréquents (spam)",
      "Objets trop longs ou peu engageants",
      "Contenu uniquement promotionnel sans valeur ajoutée",
      "Oublier le lien de désabonnement (obligatoire RGPD)"
    ]
  },
  {
    id: "social-publishing",
    title: "Publications Réseaux",
    icon: "📱",
    description: "Gestion centralisée de vos publications sur les réseaux sociaux. Créez du contenu, ajoutez votre branding et publiez sur plusieurs plateformes.",
    howToUse: [
      "Accédez via 'Publication Réseaux' sur le tableau de bord",
      "Sélectionnez le type de contenu à publier (image, vidéo, événement, etc.)",
      "Personnalisez le branding (texte, couleur, position, police)",
      "Prévisualisez le résultat",
      "Déclenchez la publication vers Make.com/Zapier"
    ],
    bestPractices: [
      "Utilisez votre branding de manière cohérente",
      "Adaptez le format au réseau (carré pour Instagram, etc.)",
      "Publiez régulièrement pour maintenir l'engagement",
      "Variez les types de contenu"
    ],
    commonMistakes: [
      "Branding incohérent d'une publication à l'autre",
      "Oublier d'adapter le format au réseau",
      "Publications trop espacées ou trop rapprochées"
    ]
  },
  {
    id: "branding",
    title: "Branding & Bonnes Pratiques",
    icon: "🎨",
    description: "Conseils pour maintenir une identité visuelle cohérente sur tous vos supports : site, emails, PDF, réseaux sociaux.",
    howToUse: [
      "Définissez votre charte graphique (couleurs, polices, logo)",
      "Uploadez votre logo dans les paramètres",
      "Utilisez les mêmes couleurs sur tous vos supports",
      "Personnalisez les templates PDF avec votre branding",
      "Appliquez le même style sur vos réseaux sociaux"
    ],
    bestPractices: [
      "Logo en haute résolution avec fond transparent",
      "Couleurs cohérentes sur tous les supports",
      "Ton de communication uniforme",
      "Signature professionnelle sur tous les documents"
    ],
    commonMistakes: [
      "Logo de mauvaise qualité ou avec fond blanc visible",
      "Couleurs différentes selon les supports",
      "Ton de communication incohérent",
      "Documents sans signature ni logo"
    ]
  },
  {
    id: "account-settings",
    title: "Paramètres du Compte",
    icon: "⚙️",
    description: "Configuration de votre compte professionnel. Gérez vos informations, sécurité, intégrations et préférences d'application.",
    howToUse: [
      "Accédez via le menu utilisateur en haut à droite → 'Paramètres du compte'",
      "Onglet 'Mon compte' : informations personnelles et praticien",
      "Uploadez votre signature, photos et certification",
      "Onglet 'Paramètres application' : configuration globale",
      "Configurez les intégrations (Stripe, email, visio, réseaux sociaux)"
    ],
    bestPractices: [
      "Complétez toutes les informations praticien",
      "Uploadez une signature propre pour les documents",
      "Testez les notifications email",
      "Vérifiez les intégrations régulièrement"
    ],
    commonMistakes: [
      "Informations incomplètes (SIRET, adresse)",
      "Signature de mauvaise qualité",
      "Intégrations non testées avant usage réel",
      "Mot de passe trop simple"
    ]
  }
];

export const faqItems = [
  {
    question: "Comment réinitialiser mon mot de passe ?",
    answer: "Rendez-vous sur la page de connexion et cliquez sur 'Mot de passe oublié'. Un email de réinitialisation vous sera envoyé."
  },
  {
    question: "Mes données sont-elles sécurisées ?",
    answer: "Oui, toutes les données sont chiffrées et stockées sur des serveurs sécurisés conformes au RGPD. L'accès est protégé par authentification."
  },
  {
    question: "Comment contacter le support ?",
    answer: "Vous pouvez nous contacter par email via les informations présentes dans les mentions légales du site."
  },
  {
    question: "Les consultants peuvent-ils accéder à leur dossier ?",
    answer: "Non, seul le praticien a accès aux dossiers. Les consultants reçoivent uniquement les documents que vous leur envoyez par email."
  },
  {
    question: "Comment exporter mes données ?",
    answer: "Chaque section permet d'exporter les données en PDF. Pour une exportation complète, contactez le support."
  },
  {
    question: "Puis-je utiliser l'application sur mobile ?",
    answer: "Oui, l'application est responsive et s'adapte à tous les écrans. Elle peut également être installée comme application (PWA)."
  }
];
